import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Calendar, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const checkPayment = async () => {
      const sessionId = searchParams.get("session_id");
      if (!sessionId) {
        setStatus("error");
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            setStatus("success");
            // Optionally fetch booking details
            // For now, just show success
          } else if (response.data.status === "expired") {
            setStatus("expired");
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, pollInterval);
          } else {
            setStatus("pending");
          }
        } catch (error) {
          console.error("Error checking payment:", error);
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, pollInterval);
          } else {
            setStatus("error");
          }
        }
      };

      poll();
    };

    checkPayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center" data-testid="booking-success-page">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-lg mx-auto text-center"
        >
          {status === "checking" && (
            <>
              <div className="w-16 h-16 mx-auto mb-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <h1 className="heading-lg mb-4">Processing Payment...</h1>
              <p className="body-text text-muted-foreground">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-8 bg-primary/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-primary" />
              </div>
              <h1 className="heading-lg mb-4 text-foreground">
                Booking <em className="italic">Confirmed!</em>
              </h1>
              <p className="body-text text-muted-foreground mb-8">
                Thank you for your booking! Your £20 deposit has been received and your appointment is confirmed. 
                A confirmation email has been sent to your inbox with all the details.
              </p>
              
              <div className="bg-muted p-8 border border-border mb-8 text-left">
                <h3 className="font-serif text-xl mb-4 text-center">What's Next?</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">1</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Check your email for your booking confirmation, payment summary, and location details (sent 24hrs before).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">2</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Arrive 10 minutes before your appointment time.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">3</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Bring your remaining balance to pay on arrival (cash or bank transfer).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">4</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Wear comfortable clothing and avoid heavy meals beforehand.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button
                    variant="outline"
                    className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    data-testid="return-home-btn"
                  >
                    Return Home
                  </Button>
                </Link>
                <Link to="/booking">
                  <Button
                    className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="book-another-btn"
                  >
                    Book Another Session
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === "pending" && (
            <>
              <div className="w-20 h-20 mx-auto mb-8 bg-secondary/20 rounded-full flex items-center justify-center">
                <Clock className="w-10 h-10 text-secondary" />
              </div>
              <h1 className="heading-lg mb-4 text-foreground">
                Payment Processing
              </h1>
              <p className="body-text text-muted-foreground mb-8">
                Your payment is being processed. You'll receive a confirmation email once 
                it's complete. If you don't receive an email within 24 hours, please contact us.
              </p>
              <Link to="/">
                <Button
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="return-home-pending-btn"
                >
                  Return Home
                </Button>
              </Link>
            </>
          )}

          {(status === "error" || status === "expired") && (
            <>
              <div className="w-20 h-20 mx-auto mb-8 bg-destructive/10 rounded-full flex items-center justify-center">
                <Calendar className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="heading-lg mb-4 text-foreground">
                {status === "expired" ? "Session Expired" : "Something Went Wrong"}
              </h1>
              <p className="body-text text-muted-foreground mb-8">
                {status === "expired"
                  ? "Your payment session has expired. Please try booking again."
                  : "We couldn't verify your payment. Please try again or contact us for assistance."}
              </p>
              <Link to="/booking">
                <Button
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="try-again-btn"
                >
                  Try Again
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
